import * as React from 'react';
import { createContext, useContext } from 'react';

import { CoachStateProvider } from 'bundles/unified-common/providers/CoachStateProvider';
import useIsDesktop from 'bundles/unified-description-page-common/hooks/useIsDesktop';
import { useTwoPanelConversationalDiscoveryExperiment } from 'bundles/unified-description-page-common/providers/specialization/TwoPanelConversationalDiscoveryExperimentProvider';

export type CoachCareerFitAssistantContextType = {
  coachCareerFitAssistantEnabled: boolean;
};

export const CoachCareerFitAssistantContext = createContext<CoachCareerFitAssistantContextType>({
  coachCareerFitAssistantEnabled: false,
});

function CoachCareerFitAssistantProvider({ children }: React.PropsWithChildren) {
  const { twoPanelConversationalDiscoveryEnabled } = useTwoPanelConversationalDiscoveryExperiment();
  const isDesktop = useIsDesktop();

  // In the two-panel treatment the docked chat replaces career-fit only while the panel can
  // actually show (≥1024px). Below that the panel container is gone (see
  // useTwoPanelPanelVisibility) and coachOpen is kept closed (CoachOpenViewportSync), so
  // career-fit takes over using the two-panel tree's CoachStateProvider — its CTA opens the
  // legacy chat via the shared coachOpen.
  const coachCareerFitAssistantEnabled = !twoPanelConversationalDiscoveryEnabled || !isDesktop;

  return (
    <CoachCareerFitAssistantContext.Provider
      value={{
        coachCareerFitAssistantEnabled,
      }}
    >
      {/* Tree shape is keyed on the UA-stable experiment flag (never the viewport-aware value
          above) so viewport changes swap context values without reparenting the page subtree.
          In treatment, career-fit consumers use the two-panel CoachStateProvider mounted above. */}
      {!twoPanelConversationalDiscoveryEnabled ? <CoachStateProvider>{children}</CoachStateProvider> : children}
    </CoachCareerFitAssistantContext.Provider>
  );
}

export const useCoachCareerFitAssistantData = () => {
  return useContext(CoachCareerFitAssistantContext);
};

export default CoachCareerFitAssistantProvider;
